import { useEffect, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { Badge, Button, EmptyState, Icon, IconButton, type IconName } from "@agentsaas/ui";
import type { Agent } from "@agentsaas/protocol";
import { useT } from "../app/i18n";
import { AgentStatusDot } from "../agent/TeamList";
import { TeamPanel } from "../team/TeamPanel";
import { FilesPane } from "../agent/AgentComputer";
import { ChatPanel } from "../chat/ChatPanel";
import { ApprovalCard } from "../approvals/ApprovalCard";
import { ApprovalList, useApprovals } from "../approvals/Approvals";
import { QuestionList, useQuestions } from "../questions/Questions";
import { FilesProvider, useFiles } from "../files/FilesContext";
import { FileView } from "../files/FileView";
import { TerminalView } from "../terminal/TerminalView";
import { SettingsPage } from "../pages/SettingsPage";
import { ShellSlot } from "../layout/shell";
import { ConversationsHub } from "../rooms/RoomsPage";
import { AdminLink } from "../admin/AdminPages";
import { useTenantLinks } from "../layout/SubFrame";
import { useProject } from "../data/queries";

/**
 * 手机端（<768）项目外壳：单栏 + 底部 tab（团队 / 对话 / 文件 / 协作 / 待处理 / 我）。
 * - 团队、对话、文件、待处理、我：在 /p/:pid[/a/:aid] 上用 ?tab= 切换；下钻（打开文件、终端）也写在查询参数里，返回手势自然生效。
 * - 对话：在 agent 页里是与该 agent 的私聊；其他页面进入会话中心 /p/:pid/rooms（聊天室 + 私聊列表）。
 * - 协作：任务 / PR / 邮件页面（/p/:pid/tasks 等），由 MobileFrame 包裹，底部 tab 相同。
 * - 待处理：审批和 agent 的提问放在一起（手机上的一等公民）。
 */
export type MobileTab = "team" | "chat" | "files" | "work" | "pending" | "me";
const TABS: { id: MobileTab; icon: IconName }[] = [
  { id: "team", icon: "users" },
  { id: "chat", icon: "chat" },
  { id: "files", icon: "file" },
  { id: "work", icon: "board" },
  { id: "pending", icon: "inbox" },
  { id: "me", icon: "user" },
];

/** 每个项目最近进入的 agent（从协作页切回对话 / 文件时回到它） */
const lastAgent = new Map<string, string>();

/** 底部 tab 栏；pendingCount = 待审批 + 待回答 */
export function MobileTabBar({ active, onSelect, pendingCount }: { active: MobileTab; onSelect(id: MobileTab): void; pendingCount: number }) {
  const { t } = useT();
  return (
    <nav className="tabbar" aria-label={t("mobile.nav")}>
      <div role="tablist" className="tabbar__list">
        {TABS.map((x) => (
          <button
            key={x.id}
            id={`mtab-btn-${x.id}`}
            role="tab"
            type="button"
            aria-selected={active === x.id}
            aria-controls={`mtab-${x.id}`}
            className={`tabbar__btn${active === x.id ? " is-active" : ""}`}
            onClick={() => onSelect(x.id)}
            data-testid={`mtab-${x.id}`}
          >
            <span className="tabbar__icon">
              <Icon name={x.icon} size={22} />
              {x.id === "pending" && pendingCount ? <span className="tabbar__badge">{pendingCount}</span> : null}
            </span>
            <span className="tabbar__label">{t(`mobile.tab.${x.id}`)}</span>
          </button>
        ))}
      </div>
    </nav>
  );
}

/** 待处理 = 待审批 + 待回答的提问 */
function usePendingCount(pid: string) {
  return useApprovals(pid).items.length + useQuestions(pid).items.length;
}

/** 从任意项目页跳到某个 tab */
export function useMobileTabNav(pid: string) {
  const navigate = useNavigate();
  return (id: MobileTab) => {
    if (id === "work") return navigate(`/p/${pid}/tasks`);
    if (id === "chat") return navigate(`/p/${pid}/rooms`);
    const aid = lastAgent.get(pid);
    navigate(`/p/${pid}${aid && id !== "team" ? `/a/${aid}` : ""}?tab=${id}`);
  };
}

/** 协作页面（任务 / PR / 邮件）在手机上的外框：内容 + 底部 tab */
export function MobileFrame({ pid, active, children }: { pid: string; active: MobileTab; children: ReactNode }) {
  const go = useMobileTabNav(pid);
  const pending = usePendingCount(pid);
  return (
    <div className="mobile">
      <div className="mobile__content" role="tabpanel" id={`mtab-${active}`} aria-labelledby={`mtab-btn-${active}`}>
        {children}
      </div>
      <MobileTabBar active={active} onSelect={go} pendingCount={pending} />
    </div>
  );
}

export function MobileProject({ pid, agent, agents }: { pid: string; agent: Agent | undefined; agents: Agent[] }) {
  const body = <MobileBody pid={pid} agent={agent} agents={agents} />;
  return agent ? (
    <FilesProvider key={agent.id} aid={agent.id}>
      {body}
    </FilesProvider>
  ) : (
    body
  );
}

function MobileBody({ pid, agent, agents }: { pid: string; agent: Agent | undefined; agents: Agent[] }) {
  const { t } = useT();
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const raw = sp.get("tab") as MobileTab | null;
  const tab: MobileTab = raw && raw !== "work" ? raw : agent ? "chat" : "team";
  const approvals = useApprovals(pid);
  const pending = usePendingCount(pid);
  const setTab = (id: MobileTab) => (id === "work" ? navigate(`/p/${pid}/tasks`) : setSp({ tab: id }, { replace: true }));
  useEffect(() => {
    if (agent) lastAgent.set(pid, agent.id);
  }, [pid, agent]);

  let content: ReactNode;
  if (tab === "team") content = <TeamPanel pid={pid} agents={agents} />;
  else if (tab === "pending") content = <PendingList pid={pid} approvals={approvals} />;
  else if (tab === "me")
    content = (
      <>
        <SettingsPage />
        <div className="page" style={{ paddingTop: 0 }}>
          <Link to="/" className="list-item card">
            <Icon name="folder" />
            <span className="grow">{t("nav.projects")}</span>
            <Icon name="chevronRight" />
          </Link>
          <TenantLinks pid={pid} />
          <AdminLink className="list-item card" />
        </div>
      </>
    );
  else if (!agent && tab === "chat") content = <ConversationsHub pid={pid} />;
  else if (!agent)
    content = (
      <EmptyState icon="users" title={t("mobile.pick_agent")}>
        <Button onClick={() => setTab("team")}>{t("mobile.tab.team")}</Button>
      </EmptyState>
    );
  else if (tab === "chat") content = <MobileChat pid={pid} agent={agent} />;
  else content = <MobileFiles agent={agent} />;

  return (
    <div className="mobile">
      {agent && (tab === "chat" || tab === "files") ? (
        <ShellSlot name="title">
          <AgentStatusDot status={agent.status} />
          <strong className="ellipsis">{agent.name}</strong>
        </ShellSlot>
      ) : null}
      <div className="mobile__content" role="tabpanel" id={`mtab-${tab}`} aria-labelledby={`mtab-btn-${tab}`}>
        {content}
      </div>
      <MobileTabBar active={tab} onSelect={setTab} pendingCount={pending} />
    </div>
  );
}

/** "待处理" tab：agent 的提问与权限审批放在一起（手机上的一等公民） */
function PendingList({ pid, approvals }: { pid: string; approvals: ReturnType<typeof useApprovals> }) {
  const { t } = useT();
  const questions = useQuestions(pid);
  return (
    <div className="page stack pending" data-testid="pending">
      <section className="stack" aria-labelledby="pending-q">
        <h2 id="pending-q" className="section-title row">
          {t("question.list_title")} <Badge tone={questions.items.length ? "warning" : "neutral"}>{questions.items.length}</Badge>
        </h2>
        {questions.items.length ? <QuestionList pid={pid} items={questions.items} nameOf={questions.nameOf} /> : <p className="muted small">{t("question.empty")}</p>}
      </section>
      <section className="stack" aria-labelledby="pending-a">
        <h2 id="pending-a" className="section-title row">
          {t("approval.list_title")} <Badge tone={approvals.items.length ? "warning" : "neutral"}>{approvals.items.length}</Badge>
        </h2>
        {approvals.items.length ? <ApprovalList pid={pid} items={approvals.items} nameOf={approvals.nameOf} /> : <p className="muted small">{t("approval.empty")}</p>}
      </section>
    </div>
  );
}

function MobileChat({ pid, agent }: { pid: string; agent: Agent }) {
  const { t } = useT();
  const approvals = useApprovals(pid).items.filter((a) => a.agentId === agent.id);
  const { files } = useFiles();
  const inline = approvals.length ? (
    <div className="stack" data-testid="inline-approvals">
      {approvals.map((a) => (
        <ApprovalCard key={a.id} approval={a} agentName={agent.name} compact />
      ))}
    </div>
  ) : null;
  return (
    <div className="mobile__dm">
      <Link to={`/p/${pid}/rooms`} className="mobile__dm-back small" data-testid="all-conversations">
        <Icon name="chevronLeft" size={16} />
        {t("room.all_conversations")}
      </Link>
      <ChatPanel agent={agent} inline={inline} openFiles={files.map((f) => f.path)} />
    </div>
  );
}

/** 文件 tab：树 → 打开文件（下钻，URL 带 file=）/ 终端（term=1） */
function MobileFiles({ agent }: { agent: Agent }) {
  const { t } = useT();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { store, files } = useFiles();
  const path = sp.get("file");
  const term = sp.get("term");
  useEffect(() => {
    if (path) void store.open(path);
  }, [path, store]);
  const back = () => navigate(-1);

  if (term)
    return (
      <div className="mobile__drill">
        <DrillHead title={t("term.title")} onBack={back} />
        <div className="mobile__drill-body">
          <TerminalView aid={agent.id} />
        </div>
      </div>
    );
  if (path) {
    const f = files.find((x) => x.path === path);
    return (
      <div className="mobile__drill">
        <DrillHead title={f?.name ?? path} onBack={back} />
        <div className="mobile__drill-body">{f ? <FileView file={f} /> : null}</div>
      </div>
    );
  }
  return (
    <div className="mobile__files">
      <FilesPane aid={agent.id} status={agent.status} onOpen={(p) => navigate({ search: `?tab=files&file=${encodeURIComponent(p)}` })} />
      <div className="mobile__files-foot">
        <Button icon="terminal" block onClick={() => navigate({ search: "?tab=files&term=1" })} data-testid="open-terminal">
          {t("term.open")}
        </Button>
      </div>
    </div>
  );
}

function DrillHead({ title, onBack }: { title: string; onBack(): void }) {
  const { t } = useT();
  return (
    <div className="drill-head">
      <IconButton icon="back" label={t("common.back")} onClick={onBack} data-testid="drill-back" />
      <strong className="ellipsis">{title}</strong>
    </div>
  );
}

/** "我" tab：当前项目所在工作区的计费 / 市场入口 */
function TenantLinks({ pid }: { pid: string }) {
  const tid = useProject(pid).data?.tenantId ?? "";
  const links = useTenantLinks(tid);
  if (!tid) return null;
  return (
    <>
      {links.map((l) => (
        <Link key={l.to} to={l.to} className="list-item card" data-testid={`me-${l.testid}`}>
          <Icon name={l.icon} />
          <span className="grow">{l.label}</span>
          <Icon name="chevronRight" />
        </Link>
      ))}
    </>
  );
}
